import ChevronDownIcon from '@mdi/svg/svg/chevron-down.svg?react';
import CloseIcon from '@mdi/svg/svg/close.svg?react';
import InformationIcon from '@mdi/svg/svg/information.svg?react';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Button, Flex, IconWithTooltip, MDIIcon } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import CameraThumbnail from 'components/CameraThumbnail/CameraThumbnail';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import CameraSelector from '../SavePanel/components/CameraSelector';
import { type SavePanelCamera } from '../SavePanel/types';
import './HorizontalCameraEditor.sass';

interface HorizontalCameraEditorProps {
  availableCameras: any[];
  canAdd: boolean;
  canEdit: boolean;
  canEditOriginal?: boolean;
  cameraSelectorRef?: React.RefObject<HTMLDivElement>;
  colorMode?: 'dark' | 'light';
  selectedCameras: { [key: string]: SavePanelCamera };
  originalCameras: any;
  renderCameraAddons?: any;
  updateCamSelection: any;
  isCameraSelectorOpen: boolean;
  isLoading?: boolean;
  isMultiSelect?: boolean;
  title?: React.ReactNode;
  updateCameraSelectorOpen: (isSelectorOpen: boolean) => void;
  debugMode?: boolean;
  getDeviceName?: (deviceId: string) => string | undefined;
  hideOverflow?: boolean;
  setEveryAlarmCamera?: (value: boolean) => void;
  everyAlarmCamera?: boolean;
  source?: string;
}

interface CameraItemProps {
  camera: any;
  cameraAddons: any;
  locale: LocaleDefinition;
  debugMode?: boolean;
  deviceName?: string;
}

const EditorCameraItem = (props: CameraItemProps) => {
  const { camera, cameraAddons, debugMode, deviceName } = props;
  const { name, chId, id, thumbnail, thumbnailType } = camera;
  return (
    <div className="camera-thumbnail-container" data-testid="camera-thumbnail-container">
      <Flex alignItems="center" className="camera-thumbnail">
        <CameraThumbnail
          cameraId={chId || id}
          thumbnail={thumbnail}
          thumbnailType={thumbnailType}
        />
        <div className="camera-thumbnail-overlay" />
        <label>
          {debugMode && deviceName && (
            <IconWithTooltip
              className="debug-info-icon"
              title={deviceName}
              icon={InformationIcon}
              iconSize={12}
              fill="var(--color-light-gray-light)"
            />
          )}
          <span className="camera-name">{name}</span>
        </label>
      </Flex>
      {cameraAddons && cameraAddons(camera)}
    </div>
  );
};

const HorizontalCameraEditor: React.FC<HorizontalCameraEditorProps> = (props) => {
  const {
    availableCameras,
    selectedCameras,
    renderCameraAddons,
    canAdd,
    canEdit,
    canEditOriginal,
    cameraSelectorRef,
    colorMode,
    originalCameras,
    updateCamSelection,
    isCameraSelectorOpen,
    isLoading = false,
    isMultiSelect = true,
    title,
    updateCameraSelectorOpen,
    debugMode,
    getDeviceName,
    hideOverflow = false,
    setEveryAlarmCamera,
    everyAlarmCamera,
    source,
  } = props;
  const locale = useLocale();

  const [newSelectedCameras, setNewSelectedCameras] = useState<{ [key: string]: any }>({});
  const [prevIncludedAlarmCameras, setPrevIncludedAlarmCameras] = useState<boolean | undefined>(
    undefined,
  );

  const cameraSelectorVisible = isCameraSelectorOpen && availableCameras && canAdd;

  const addRemoveCameraButtonClass = classNames('add-remove-camera-button', {
    active: canAdd,
    remove: cameraSelectorVisible,
  });

  const openCameraSelector = useCallback(() => {
    updateCameraSelectorOpen(true);
    setNewSelectedCameras(cloneDeep(selectedCameras));
    // Save the original state of everyAlarmCamera to restore on cancel
    if (setEveryAlarmCamera && everyAlarmCamera !== undefined) {
      setPrevIncludedAlarmCameras(everyAlarmCamera);
    }
  }, [selectedCameras, updateCameraSelectorOpen, setEveryAlarmCamera, everyAlarmCamera]);

  const closeCameraSelector = useCallback(() => {
    updateCameraSelectorOpen(false);
    setNewSelectedCameras({});
    // Restore the original state of everyAlarmCamera on cancel
    if (setEveryAlarmCamera && prevIncludedAlarmCameras !== undefined) {
      setEveryAlarmCamera(prevIncludedAlarmCameras);
      setPrevIncludedAlarmCameras(undefined);
    }
  }, [updateCameraSelectorOpen, setEveryAlarmCamera, prevIncludedAlarmCameras]);

  const submitCameraSelector = useCallback(() => {
    updateCamSelection(newSelectedCameras);
    updateCameraSelectorOpen(false);
    // Update the flag based on whether all available cameras are selected
    if (setEveryAlarmCamera) {
      const allCamerasSelected = Object.keys(newSelectedCameras).length === availableCameras.length;
      setEveryAlarmCamera(allCamerasSelected);
    }
    setNewSelectedCameras({});
    // Clear the saved original state since changes were committed
    setPrevIncludedAlarmCameras(undefined);
  }, [
    newSelectedCameras,
    updateCamSelection,
    updateCameraSelectorOpen,
    setEveryAlarmCamera,
    availableCameras,
  ]);

  const toggleCamSelection = useCallback(
    (camera: SavePanelCamera) => {
      const cameraId = camera.id;
      let selectedCameras = cloneDeep(newSelectedCameras);

      if (isMultiSelect) {
        if (selectedCameras[cameraId]) {
          delete selectedCameras[cameraId];
        } else {
          selectedCameras[cameraId] = camera;
        }
      } else {
        selectedCameras = { [cameraId]: camera };
      }

      setNewSelectedCameras(selectedCameras);
    },
    [newSelectedCameras, isMultiSelect],
  );

  const camerasCardClass = classNames('cameras-card', {
    'camera-selector-visible': cameraSelectorVisible,
    'dark-mode': colorMode === 'dark',
  });

  const displayCloseIcon = isMultiSelect || cameraSelectorVisible;
  const cameraSelectorIcon = displayCloseIcon ? CloseIcon : ChevronDownIcon;
  const cameraSelectorIconClass = classNames({ 'close-icon': displayCloseIcon });
  const cameraSelectorIconTooltip = cameraSelectorVisible
    ? locale.general.close
    : isMultiSelect
      ? locale.camera.addCamera
      : locale.cameraSelector.chooseCamera;

  const loadingOverlayStyles: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    background: 'rgba(255, 255, 255, 0.5)',
  };

  return (
    <>
      <div className={camerasCardClass}>
        {title && title}
        <Flex alignItems="center">
          <Flex className="horizontal-camera-editor" alignItems="center">
            {Object.keys(selectedCameras).map((camId, index) => {
              const camera = selectedCameras[camId];
              const deviceName =
                debugMode && getDeviceName ? getDeviceName(camera.deviceId) : undefined;
              return (
                <EditorCameraItem
                  cameraAddons={renderCameraAddons}
                  camera={camera}
                  key={index}
                  locale={locale}
                  debugMode={debugMode}
                  deviceName={deviceName}
                />
              );
            })}
          </Flex>
          {canEdit && (
            <Button
              className={addRemoveCameraButtonClass}
              dataIntercomTarget="add-remove-camera-button"
              onClick={cameraSelectorVisible ? closeCameraSelector : openCameraSelector}
              type="clear"
              iconOnly={true}
              icon={
                <MDIIcon
                  icon={cameraSelectorIcon}
                  className={cameraSelectorIconClass}
                  fill={colorMode === 'dark' ? 'var(--color-white)' : 'var(--color-black)'}
                />
              }
              tooltip={cameraSelectorIconTooltip}
              tooltipStyle={{ minWidth: '105px' }}
            />
          )}
        </Flex>
        {isLoading && <CenteredLoadingSpinner style={loadingOverlayStyles} />}
      </div>
      <CameraSelector
        visible={cameraSelectorVisible}
        locale={locale}
        availableCameras={availableCameras}
        hideOverflow={hideOverflow}
        canEditOriginal={canEditOriginal}
        cameraSelectorRef={cameraSelectorRef}
        colorMode={colorMode}
        isMultiSelect={isMultiSelect}
        originalCameras={originalCameras}
        newSelectedCameras={newSelectedCameras}
        cancelCameraSelector={closeCameraSelector}
        submitCameraSelector={submitCameraSelector}
        toggleCamSelection={toggleCamSelection}
        updateCamSelection={setNewSelectedCameras}
        debugMode={debugMode}
        getDeviceName={debugMode ? getDeviceName : undefined}
        setEveryAlarmCamera={setEveryAlarmCamera}
        source={source}
      />
    </>
  );
};

export default HorizontalCameraEditor;
